import React, { Component, ReactNode } from 'react';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { captureEnhancedErrorContext } from '@lib/utils/enhancedErrorContext';
import { ErrorFallbackUI } from '@components/ErrorBoundary/ErrorFallbackUI';
import * as Sentry from '@sentry/react';
import AuthModule from '@modules/AuthModule';
import FullStoryModule from '@modules/FullStoryModule';
import environment from '@lib/environment';

export type FeatureErrorBoundarySize = 'full' | 'compact';

export interface FeatureErrorBoundaryProps {
  children: ReactNode;
  featureName: string;
  /**
   * Size variant for the error fallback UI
   * - 'full': Large fallback with 400px min-height, suitable for full-page features (default)
   * - 'compact': Smaller fallback with flexible height, suitable for cards and small components
   */
  size?: FeatureErrorBoundarySize;
  /**
   * Maximum number of resets allowed within resetWindow (default: 5)
   */
  maxResets?: number;
  /**
   * Time window in ms to track resets (default: 30000ms = 30 seconds)
   */
  resetWindow?: number;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  resetTimestamps: number[];
  maxResetsExceeded: boolean;
}

/**
 * FeatureErrorBoundary - Error boundary for lazy-loaded route features and components
 *
 * Features:
 * - Catches errors in lazy-loaded route components and granular page components
 * - Shows inline error within the route/component area
 * - Reports errors to DiagnosticsModule
 * - Provides retry functionality with reset limits
 * - Two size variants: 'full' for pages, 'compact' for cards
 * - Uses Anvil v2 components for consistency
 * - Disabled in development mode (shows React overlay) unless VITE_ENABLE_ERROR_BOUNDARIES=true
 *
 * Usage:
 * Full-page features (default):
 * @example
 * <FeatureErrorBoundary featureName="Settings">
 *   <SettingsPage />
 * </FeatureErrorBoundary>
 *
 * Card or small components:
 * @example
 * <FeatureErrorBoundary featureName="Capacity Card" size="compact">
 *   <CallCenterCapacity />
 * </FeatureErrorBoundary>
 */
export class FeatureErrorBoundary extends Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      resetTimestamps: [],
      maxResetsExceeded: false,
    };
  }

  static getDerivedStateFromError(
    error: Error,
  ): Partial<FeatureErrorBoundaryState> {
    // Check if error boundaries should be enabled in development
    const enableInDev = import.meta.env.VITE_ENABLE_ERROR_BOUNDARIES === 'true';

    // In local development, don't catch UNLESS explicitly enabled for testing
    if (environment.MODE === 'development' && !enableInDev) {
      // Debug log to help understand error boundary behavior in dev mode
      if (environment.MODE === 'development') {
        console.debug(
          '[FeatureErrorBoundary] Passing through error in dev mode (boundaries disabled)',
        );
      }
      return {}; // Return empty state, don't set hasError - show React overlay
    }

    // Debug log for when boundaries are enabled
    if (environment.MODE === 'development') {
      console.debug(
        '[FeatureErrorBoundary] Catching error (boundaries enabled)',
      );
    }

    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { featureName } = this.props;

    try {
      // Always log for debugging (helpful in all environments)
      console.error(
        `[FeatureErrorBoundary:${featureName}] caught error:`,
        error,
        errorInfo,
      );

      // Capture enhanced error context (url, pathname, feature flags, etc.)
      const enhancedContext = captureEnhancedErrorContext();

      // Enhance Sentry context with additional information for error boundaries
      const user = AuthModule.user;
      if (user) {
        Sentry.setTags({
          'org.id': user.org.id,
          'org.name': user.org.name,
          accountId: user.accountId,
        });

        Sentry.setUser({
          id: user.id,
          email: user.email,
          username: user.name,
        });
      }

      // Add FullStory session URL as context
      if (FullStoryModule.sessionUrl) {
        Sentry.setContext('fullstory', {
          sessionUrl: FullStoryModule.sessionUrl,
        });
      }

      // Add feature flags as context
      Sentry.setContext('featureFlags', enhancedContext.featureFlags);

      // Report error to DiagnosticsModule as background error
      // Feature failures are less critical than global failures
      DiagnosticsModule.reportBackgroundError(
        error,
        `Feature Error (${featureName}): ${error.message}`,
        {
          priority: 'normal',
          source: `feature-error-boundary-${featureName.toLowerCase()}`,
          extra: {
            featureName,
            componentStack: errorInfo.componentStack,
            errorInfo,
            // Enhanced context for better debugging
            url: enhancedContext.url,
            pathname: enhancedContext.pathname,
            timestamp: enhancedContext.timestamp,
            userAgent: enhancedContext.userAgent,
            featureFlags: enhancedContext.featureFlags,
          },
        },
      );
    } catch (handlerError) {
      // Critical: Error handlers must NEVER throw, or they break error isolation
      // If error reporting fails, just log it and continue
      console.error(
        `[FeatureErrorBoundary:${featureName}] Error handler failed:`,
        handlerError,
      );
      console.error('Original error:', error);
    }

    // Note: Environment-based error boundary behavior is controlled in getDerivedStateFromError,
    // not here. componentDidCatch should only perform side effects (logging, reporting).
    // Re-throwing here would corrupt React's internal state.
  }

  reset = (): void => {
    const { maxResets = 5, resetWindow = 30000 } = this.props;
    const now = Date.now();

    // Filter out timestamps older than resetWindow
    const recentTimestamps = this.state.resetTimestamps.filter(
      (timestamp) => now - timestamp < resetWindow,
    );

    // Safety limit: Cap at 10 timestamps to prevent memory issues
    const MAX_TIMESTAMPS = 10;
    const limitedTimestamps =
      recentTimestamps.length > MAX_TIMESTAMPS
        ? recentTimestamps.slice(-MAX_TIMESTAMPS)
        : recentTimestamps;

    // Check if we've exceeded maxResets within the resetWindow
    if (limitedTimestamps.length >= maxResets) {
      // Max resets exceeded - show permanent error
      this.setState({
        maxResetsExceeded: true,
      });
      return;
    }

    // Reset is allowed - add timestamp and reset error state
    this.setState({
      hasError: false,
      error: null,
      resetTimestamps: [...limitedTimestamps, now],
      maxResetsExceeded: false,
    });
  };

  render(): ReactNode {
    const { children, size = 'full', featureName } = this.props;

    if (this.state.hasError) {
      const { maxResetsExceeded } = this.state;

      // If max resets exceeded, show permanent error
      if (maxResetsExceeded) {
        return (
          <ErrorFallbackUI
            title={`${featureName} Error`}
            message={
              size === 'full'
                ? 'This feature has crashed multiple times and cannot be recovered. Please refresh the page.'
                : 'crashed. Please refresh.'
            }
            severity='error'
            layout='centered'
            size={size}
            permanent={true}
            testId={
              size === 'compact'
                ? 'feature-error-permanent-fallback-compact'
                : 'feature-error-permanent-fallback'
            }
          />
        );
      }

      // Normal error fallback
      return (
        <ErrorFallbackUI
          title={`${featureName} Error`}
          message={
            size === 'full'
              ? "This feature encountered an error and couldn't load."
              : 'Unable to load this section.'
          }
          severity='error'
          layout='centered'
          size={size}
          onRetry={this.reset}
          testId={
            size === 'compact'
              ? 'feature-error-fallback-compact'
              : 'feature-error-fallback'
          }
        />
      );
    }

    return children;
  }
}
